import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Card, CardContent,
  Chip, Tabs, Tab, TextField, Select, MenuItem, FormControl, InputLabel,
  ToggleButton, ToggleButtonGroup, RadioGroup, FormControlLabel, Radio,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Alert, CircularProgress, Snackbar, Divider, Grid, Autocomplete,
} from '@mui/material';
import { Send, Notifications, Campaign } from '@mui/icons-material';
import {
  broadcastNotification, sendTargetedNotification, fetchAllNotifications,
  fetchCampaigns, fetchSegments,
} from '../../api/api';

const EMPTY_FORM = {
  type: 'push', target: 'all', segmentId: '', userId: '',
  title: '', body: '', imageUrl: '', deepLink: '',
};

const TYPE_COLORS = {
  push: '#667eea', email: '#10b981', sms: '#f59e0b', 'in-app': '#8b5cf6',
};

export default function NotificationCenter() {
  const [tab, setTab] = useState('send');
  const [form, setForm] = useState(EMPTY_FORM);
  const [segments, setSegments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [histLoading, setHistLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  useEffect(() => {
    fetchSegments().then(r => setSegments(r.data?.segments || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab]);

  const loadHistory = async () => {
    setHistLoading(true);
    try {
      const res = await fetchAllNotifications({ limit: 50 });
      setNotifications(res.data?.notifications || res.data || []);
    } catch {
      setNotifications([]);
    } finally {
      setHistLoading(false);
    }
  };

  const handleSend = async () => {
    if (!form.title.trim()) return showSnack('Title is required', 'error');
    if (!form.body.trim()) return showSnack('Message is required', 'error');
    setSending(true);
    setSendResult(null);
    setError('');
    try {
      const payload = {
        type: form.type,
        title: form.title,
        body: form.body,
        ...(form.imageUrl && { imageUrl: form.imageUrl }),
        ...(form.deepLink && { deepLink: form.deepLink }),
      };
      let res;
      if (form.target === 'all') {
        res = await broadcastNotification(payload);
      } else {
        res = await sendTargetedNotification({
          ...payload,
          targetType: form.target,
          ...(form.target === 'segment' && { segmentId: form.segmentId }),
          ...(form.target === 'user' && { userId: form.userId }),
        });
      }
      const stats = res.data?.stats || res.data;
      setSendResult(stats);
      showSnack(`Notification sent! Delivered to ${stats?.sent || stats?.count || 'users'}`);
      setForm(EMPTY_FORM);
    } catch (e) {
      setError(e.response?.data?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const handleBroadcast = () => {
    setForm(f => ({ ...f, target: 'all' }));
    setTab('send');
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="text.primary">Notification Center</Typography>
          <Typography variant="body2" color="text.secondary">Send push, in-app, email, and SMS notifications</Typography>
        </Box>
        <Button variant="contained" startIcon={<Campaign />} onClick={handleBroadcast}
          sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700 }}>
          Broadcast to All
        </Button>
      </Box>

      <Paper sx={{ bgcolor: 'background.paper', borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="send" label="Send Notification" icon={<Send />} iconPosition="start" />
          <Tab value="history" label="Notification History" icon={<Notifications />} iconPosition="start" />
        </Tabs>
      </Paper>

      {tab === 'send' && (
        <Card sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3, maxWidth: 700 }}>
          <CardContent>
            {sendResult && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSendResult(null)}>
                Notification sent successfully!
                {sendResult.sent !== undefined && ` Sent to ${sendResult.sent} users.`}
                {sendResult.failed !== undefined && ` Failed: ${sendResult.failed}.`}
              </Alert>
            )}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Notification Type</Typography>
                <ToggleButtonGroup value={form.type} exclusive onChange={(_, v) => v && setForm(f => ({ ...f, type: v }))} size="small">
                  {['push', 'in-app', 'email', 'sms'].map(t => (
                    <ToggleButton key={t} value={t} sx={{ textTransform: 'capitalize', fontWeight: 600, px: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TYPE_COLORS[t] }} />
                        {t}
                      </Box>
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Target Audience</Typography>
                <RadioGroup row value={form.target} onChange={e => setForm(f => ({ ...f, target: e.target.value }))}>
                  <FormControlLabel value="all" control={<Radio />} label="All Users" />
                  <FormControlLabel value="segment" control={<Radio />} label="Segment" />
                  <FormControlLabel value="user" control={<Radio />} label="Specific User" />
                </RadioGroup>
                {form.target === 'segment' && (
                  <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <InputLabel>Select Segment</InputLabel>
                    <Select value={form.segmentId} label="Select Segment" onChange={e => setForm(f => ({ ...f, segmentId: e.target.value }))}>
                      {segments.map(s => <MenuItem key={s._id} value={s._id}>{s.name} ({s.memberCount || 0} members)</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                {form.target === 'user' && (
                  <TextField
                    label="User Email or Phone"
                    value={form.userId}
                    onChange={e => setForm(f => ({ ...f, userId: e.target.value }))}
                    fullWidth size="small" sx={{ mt: 1 }}
                    placeholder="user@example.com or +254..."
                  />
                )}
              </Box>

              <Divider />

              <TextField label="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} fullWidth />
              <TextField label="Message Body *" value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                multiline rows={4} fullWidth />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Image URL (optional)" value={form.imageUrl}
                    onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))} fullWidth />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Deep Link (optional)" value={form.deepLink}
                    onChange={e => setForm(f => ({ ...f, deepLink: e.target.value }))} fullWidth
                    placeholder="screen://product/123" />
                </Grid>
              </Grid>

              <Button
                variant="contained"
                size="large"
                startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <Send />}
                onClick={handleSend}
                disabled={sending}
                sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', fontWeight: 700, alignSelf: 'flex-start', px: 4 }}
              >
                {sending ? 'Sending...' : 'Send Notification'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {tab === 'history' && (
        <>
          {histLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : (
            <Paper sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Target</TableCell>
                      <TableCell align="right">Sent To</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notifications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Box sx={{ py: 4 }}>
                            <Notifications sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                            <Typography color="text.secondary">No notification history</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      notifications.map((n, i) => (
                        <TableRow key={n._id || i} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{n.title || n.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{n.body || n.content?.body}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={n.type || 'push'} size="small"
                              sx={{ bgcolor: TYPE_COLORS[n.type] || '#667eea', color: '#fff', fontWeight: 700, fontSize: 10 }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {n.targetAudience?.type === 'all' ? 'All Users' : n.targetAudience?.type || 'All'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{n.stats?.sent || n.sentCount || '-'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {n.createdAt ? new Date(n.createdAt).toLocaleString() : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={n.status || 'sent'} size="small"
                              color={n.status === 'sent' ? 'success' : n.status === 'failed' ? 'error' : 'default'} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
